import { Types } from './header';

const INITIAL_STATE = {
  metadata: [],
  missions: [],
};

const reduceAvailableDates = (state, payload) => {
  const metadataMap = {};
  state.metadata.forEach((m) => {
    metadataMap[`${m.date}-${m.missionName}`] = m.clouds;
  });
  const normalizedDates = payload.dates.map((d, idx) => {
    const key = `${d.date}-${d.name}`;
    const clouds = metadataMap[key];
    const normalized = {
      ...d,
      content: clouds !== undefined ? clouds : d.content || 0,
    };
    return normalized;
  });
  if (payload.missions !== undefined) {
    return {
      ...state,
      availableDates: normalizedDates,
      missions: payload.missions,
    };
  }
  if (state.missions !== undefined) {
    const available = normalizedDates;
    let missions = [];
    state.missions.forEach((mission) => {
      const dates = mission.dates;
      let filteredDates = {};
      Object.keys(dates).forEach((date) => {
        const cur = available.find((value) => value.date === date);
        if (cur) filteredDates[date] = dates[date];
      });
      missions.push({
        name: mission.name,
        shortname: mission.shortname,
        dates: filteredDates,
      });
    });
    const metadata = state.metadata.filter((meta) => {
      return available.find((value) => value.date === meta.date && value.name === meta.missionName);
    });
    return { ...state, metadata, availableDates: normalizedDates, missions };
  }
  return { ...state, availableDates: normalizedDates };
};

const reduceMissionFallback = (state, action) => {
  if (state.satellite) {
    const { mission } = action;

    const updated = state.satellite.missions.map((m) => (m.name === mission.name ? mission.fallbackMission : m));

    const satellite = {
      ...state.satellite,
      missions: updated,
      get: (name) => {
        const [first] = updated.filter((mission) => mission.name === name);
        return first;
      },
    };

    return { ...state, satellite };
  }
  return state;
};

export const reducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case Types.SET_SATELLITE:
      const { satellite, satelliteIndex } = action.payload;
      return { ...state, satellite, satelliteIndex };

    case Types.SET_MISSION_FALLBACK:
      return reduceMissionFallback(state, action.payload);

    case Types.SET_AOI:
      const { overlay, geometry, coordinates, kmlFileName } = action.payload;
      return { ...state, overlay, geometry, coordinates, kmlFileName };

    /**
     * `availableDatesVersion` counts completed searches. `PeriodImageChooser` watches it to know
     * a search has come back, which it cannot tell from `availableDates` alone: that selector
     * uses `shallowEqual`, so a search returning no images right after another empty result hands
     * back the very same array identity, no effect re-runs, and the component is left believing
     * it is still searching - the search button stays disabled and the "no images found" message
     * never appears. The counter always changes, empty result or not.
     */
    case Types.SET_AVAILABLE_DATES:
      return {
        ...reduceAvailableDates(state, action.payload),
        availableDatesVersion: (state.availableDatesVersion || 0) + 1,
      };

    case Types.SET_PERIOD:
      return { ...state, start: action.payload.start, end: action.payload.end };

    /**
     * One thumbnail has come back. Only the image it belongs to is rewritten.
     * This used to rebuild the cloud-cover map across all metadata and then copy every entry of
     * `availableDates` into a fresh object - on each of the arrivals, six of which are in flight at
     * a time. The work itself was the smaller half of the cost: giving every image a new identity
     * told React that every row and every card had changed, so the whole list re-rendered dozens of
     * times while the thumbnails streamed in, which is what made clicking one feel stuck. Images
     * other than this one now keep the identity they had, and the list is left alone when the value
     * it would write is the one already there.
     */
    case Types.INSERT_METADATA: {
      const { missionName, date } = action.payload;
      const entry = { ...action.payload.metadata, missionName, date };
      const metadata = [...state.metadata, entry];
      const { clouds } = entry;

      let changed = false;
      const availableDates = (state.availableDates || []).map((image) => {
        if (image.date !== date || image.name !== missionName) return image;
        if (clouds === undefined || image.content === clouds) return image;
        changed = true;
        return { ...image, content: clouds };
      });

      return { ...state, metadata, availableDates: changed ? availableDates : state.availableDates };
    }

    case Types.RESET:
      return INITIAL_STATE;

    default:
      return state;
  }
};

export default reducer;
